import React, { FC } from 'react';
import { Pill } from '@clearcover/atlas';
import { HRule } from 'Shared/components';
import { useAppContext } from 'QuoteApp/services/providers';
import { EditProvider } from 'QuoteApp/utils/contexts';
import { useReportCompliance } from 'QuoteApp/utils/hooks/useReportCompliance';
import { CurrentCarrierReport } from './Reports/CurrentCarrier/CurrentCarrierReport';
import { DriverLicenseReport } from './Reports/DriverLicense/DriverLicenseReport';
import { ViolationReport } from './Reports/Violations/ViolationsReport';
import { AccidentsReport } from './Reports/Accidents/AccidentsReport';
import { DriverName } from './DriverName';
import { DRIVER_ORDER } from '../DriversList';
import {
  DriverDataType,
  GetDetailsResponseType,
  ReportType,
  ThirdPartyReports,
} from 'QuoteApp/services/endpoints/GetDetails';
import styles from './styles.module.css';
import { DriverReportType } from './types';
import { NonListedDriversReport } from './NonListedDrivers';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';
import { PriorPolicies } from './Reports/PriorPolicies';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';

type ReportsByName = { [key in ThirdPartyReports]?: ReportType };

type ComponentConfig = {
  template: DriverReportType;
  title: string;
  condition: (
    reports: ReportsByName,
    currentDriver: DriverDataType,
    config: GetConfigResponseType
  ) => boolean;
};

type DriverRunReportStatus = {
  badgeText: string;
  messageText: string;
};

const hasAnyRun = (
  reports: ReportsByName,
  ...names: Array<ThirdPartyReports>
): boolean => names.some(name => reports[name]?.status === 'has_run');

const driverReportSections: Array<ComponentConfig> = [
  {
    template: PriorPolicies,
    title: 'Current Carrier',
    condition: (reports, currentDriver, config) =>
      hasAnyRun(reports, ThirdPartyReports.NcfAndCurrentCarrier) &&
      currentDriver.driverType === 'principalInsured' &&
      config.drivers.priorPolicies?.required,
  },
  {
    template: CurrentCarrierReport,
    title: 'Current Carrier',
    condition: (reports, currentDriver, config) =>
      hasAnyRun(reports, ThirdPartyReports.NcfAndCurrentCarrier) &&
      currentDriver.driverType === 'principalInsured' &&
      !config.drivers.priorPolicies?.required,
  },
  {
    template: AccidentsReport,
    title: 'Accidents',
    condition: (reports, currentDriver) =>
      hasAnyRun(reports, ThirdPartyReports.Clue, ThirdPartyReports.All) &&
      currentDriver.driverType !== 'excludedPerson' &&
      currentDriver.driverType !== 'listOnly',
  },
  {
    template: ViolationReport,
    title: 'Violations',
    condition: (reports, currentDriver) =>
      hasAnyRun(reports, ThirdPartyReports.All) &&
      currentDriver.driverType !== 'excludedPerson' &&
      currentDriver.driverType !== 'listOnly',
  },
  {
    template: DriverLicenseReport,
    title: 'Driver License',
    condition: (reports, currentDriver) =>
      hasAnyRun(reports, ThirdPartyReports.All) &&
      currentDriver.driverType !== 'excludedPerson' &&
      currentDriver.driverType !== 'listOnly',
  },
];

const reportDataForDriver: (
  reportsByName: ReportsByName,
  currentDriver: DriverDataType,
  config: GetConfigResponseType
) => Array<ComponentConfig> = (reportsByName, currentDriver, config) => {
  return driverReportSections.filter(componentConfig =>
    componentConfig.condition(reportsByName, currentDriver, config)
  );
};

const groupReportsByName = (details: GetDetailsResponseType): ReportsByName => {
  return details.commands.reports.reduce((grouped: ReportsByName, report) => {
    grouped[report.reportName] = report;
    return grouped;
  }, {});
};

const driverRunReportsStatuses: Record<string, DriverRunReportStatus> = {
  excludedPerson: {
    badgeText: 'Excluded',
    messageText: 'Reports are not run on excluded drivers.',
  },
  listOnly: {
    badgeText: 'List-Only',
    messageText: 'Reports are not run on list-only drivers.',
  },
};

export const ReportDetails: FC = () => {
  const { details, config } = useAppContext();
  const { canShowReportData } = useReportCompliance();

  const { showLendingLossesFlag } = useQuoteAppFeatureFlags();

  if (!config || !canShowReportData) return null;

  const reportsByName = groupReportsByName(details);
  if (
    !hasAnyRun(
      reportsByName,
      ThirdPartyReports.NcfAndCurrentCarrier,
      ThirdPartyReports.All
    )
  )
    return null;

  return (
    <>
      <EditProvider>
        {() =>
          Object.keys(details.drivers)
            .sort((a: string, b: string) => {
              return (
                DRIVER_ORDER.indexOf(details.drivers[a].driverType) -
                DRIVER_ORDER.indexOf(details.drivers[b].driverType)
              );
            })
            .map(currentDriverId => {
              const currentDriver = details.drivers[currentDriverId];
              const { badgeText, messageText } =
                driverRunReportsStatuses?.[currentDriver?.driverType] || {};
              const reportComponents = reportDataForDriver(
                reportsByName,
                currentDriver,
                config
              );
              const isExcludedDriver =
                currentDriver.driverType === 'excludedPerson';
              const isListOnlyDriver = currentDriver.driverType === 'listOnly';

              if (
                reportComponents.length === 0 &&
                !isExcludedDriver &&
                !isListOnlyDriver
              )
                return null;

              return (
                <div
                  key={currentDriver.identifier}
                  className={styles.container}
                  data-testid="report-details"
                >
                  <div className={styles.nameWrapper}>
                    <DriverName
                      driver={currentDriver}
                      driverConfig={config.drivers}
                    />
                    {badgeText && (
                      <Pill variant="disabled" className={styles.statusBadge}>
                        {badgeText}
                      </Pill>
                    )}
                  </div>
                  {messageText && (
                    <div className={styles.statusMessage}>{messageText}</div>
                  )}
                  {reportComponents.map((component, index) => {
                    return (
                      <div key={`report-data-component-${index}`}>
                        {index > 0 && <HRule className={styles.divider} />}
                        <h6 className={styles.reportTitle}>
                          {component.title}
                        </h6>
                        <component.template
                          driverId={currentDriver.identifier}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })
        }
      </EditProvider>
      {showLendingLossesFlag && <NonListedDriversReport />}
    </>
  );
};
